import { Injectable, inject } from '@angular/core';
import { HttpClient, HttpParams } from '@angular/common/http';
import { Observable, of } from 'rxjs';
import { map, catchError } from 'rxjs/operators';
import { environment } from '../../environments/environment';
import {
  getForecastRange,
  type ForecastHorizon,
  type ForecastPeriod
} from './forecast-cash-flow.service';

export type { ForecastHorizon, ForecastPeriod };

export const PIPELINE_PROBABILITY: Record<string, number> = {
  ACCEPTED: 0.9,
  CONTRACT_ATTACHMENT: 0.7,
  OC: 0.85,
  INVOICED: 0.95,
  INVOICE_IMPORTED: 0.97,
  PAID: 1,
  LOST: 0
};

export const PIPELINE_STAGE_ORDER = [
  'ACCEPTED',
  'CONTRACT_ATTACHMENT',
  'OC',
  'INVOICED',
  'INVOICE_IMPORTED',
  'PAID',
  'LOST'
] as const;

export interface PipelineSaleRow {
  id: number;
  number?: string;
  customerName: string;
  processStatus: string;
  saleType?: string;
  total: number;
  probability: number;
  weightedTotal: number;
  createdAt?: string;
  issueDate?: string;
  daysInStage: number;
}

export interface PipelineStageBucket {
  status: string;
  label: string;
  count: number;
  amount: number;
  weightedAmount: number;
  probability: number;
}

export interface PipelineKpis {
  pipelineAmount: number;
  pipelineCount: number;
  weightedAmount: number;
  avgProbability: number;
  acceptedAmount: number;
  acceptedCount: number;
  lostAmount: number;
  lostCount: number;
  winRate: number;
  createdInPeriod: number;
}

export interface PipelineForecastData {
  from: string;
  to: string;
  kpis: PipelineKpis;
  stages: PipelineStageBucket[];
  bySaleType: { saleType: string; count: number; amount: number; weightedAmount: number }[];
  openDeals: PipelineSaleRow[];
  lostDeals: PipelineSaleRow[];
  loading: boolean;
  error?: string;
}

interface PipelineApi {
  from?: string;
  to?: string;
  kpis?: Partial<PipelineKpis>;
  stages?: Array<Partial<PipelineStageBucket>>;
  bySaleType?: Array<{ saleType?: string; count?: number; amount?: number; weightedAmount?: number }>;
  openDeals?: Array<Partial<PipelineSaleRow>>;
  lostDeals?: Array<Partial<PipelineSaleRow>>;
}

interface WrapperPipeline {
  ok?: boolean;
  message?: string;
  body?: PipelineApi | null;
}

function num(v: unknown): number {
  const n = Number(v);
  return Number.isFinite(n) ? n : 0;
}

function toDayKey(d: Date): string {
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, '0');
  const day = String(d.getDate()).padStart(2, '0');
  return `${y}-${m}-${day}`;
}

function mapSaleRow(r: Partial<PipelineSaleRow>): PipelineSaleRow {
  return {
    id: num(r.id),
    number: r.number,
    customerName: r.customerName || '—',
    processStatus: r.processStatus || 'OC',
    saleType: r.saleType,
    total: num(r.total),
    probability: num(r.probability),
    weightedTotal: num(r.weightedTotal),
    createdAt: r.createdAt,
    issueDate: r.issueDate,
    daysInStage: num(r.daysInStage)
  };
}

export function pipelineStageLabel(status: string): string {
  const map: Record<string, string> = {
    ACCEPTED: 'Aceptado',
    CONTRACT_ATTACHMENT: 'Contrato/Anexo',
    OC: 'OC',
    INVOICED: 'Facturado',
    INVOICE_IMPORTED: 'Factura Ingresada',
    PAID: 'Pagado',
    LOST: 'Perdido'
  };
  return map[status] || status;
}

export function saleTypeLabel(saleType: string | undefined): string {
  if (saleType === 'SUBSCRIPTION') return 'Suscripción';
  if (saleType === 'ONE_TIME') return 'Única';
  return saleType || '—';
}

@Injectable({ providedIn: 'root' })
export class ForecastPipelineService {
  private http = inject(HttpClient);
  private apiUrl = environment.apiUrl;

  load(period: ForecastPeriod = { horizon: '6m' }): Observable<PipelineForecastData> {
    const { from, to } = getForecastRange(period);
    const params = new HttpParams().set('from', toDayKey(from)).set('to', toDayKey(to));

    return this.http.get<WrapperPipeline>(`${this.apiUrl}/forecasts/pipeline`, { params }).pipe(
      map((res) => {
        if (!res?.ok || !res.body) {
          return this.empty(from, to, res?.message || 'No se pudo cargar el forecast de pipeline');
        }
        return this.mapApi(res.body);
      }),
      catchError((err) =>
        of(this.empty(from, to, err?.error?.message || err?.message || 'Error al cargar el forecast de pipeline'))
      )
    );
  }

  private mapApi(body: PipelineApi): PipelineForecastData {
    const k = body.kpis ?? {};
    return {
      from: body.from ?? '',
      to: body.to ?? '',
      kpis: {
        pipelineAmount: num(k.pipelineAmount),
        pipelineCount: num(k.pipelineCount),
        weightedAmount: num(k.weightedAmount),
        avgProbability: num(k.avgProbability),
        acceptedAmount: num(k.acceptedAmount),
        acceptedCount: num(k.acceptedCount),
        lostAmount: num(k.lostAmount),
        lostCount: num(k.lostCount),
        winRate: num(k.winRate),
        createdInPeriod: num(k.createdInPeriod)
      },
      stages: (body.stages ?? []).map((s) => ({
        status: s.status || '',
        label: s.label || pipelineStageLabel(s.status || ''),
        count: num(s.count),
        amount: num(s.amount),
        weightedAmount: num(s.weightedAmount),
        probability: num(s.probability)
      })),
      bySaleType: (body.bySaleType ?? []).map((r) => ({
        saleType: r.saleType || '—',
        count: num(r.count),
        amount: num(r.amount),
        weightedAmount: num(r.weightedAmount)
      })),
      openDeals: (body.openDeals ?? []).map(mapSaleRow),
      lostDeals: (body.lostDeals ?? []).map(mapSaleRow),
      loading: false
    };
  }

  private empty(from: Date, to: Date, error?: string): PipelineForecastData {
    return {
      from: toDayKey(from),
      to: toDayKey(to),
      kpis: {
        pipelineAmount: 0,
        pipelineCount: 0,
        weightedAmount: 0,
        avgProbability: 0,
        acceptedAmount: 0,
        acceptedCount: 0,
        lostAmount: 0,
        lostCount: 0,
        winRate: 0,
        createdInPeriod: 0
      },
      stages: [],
      bySaleType: [],
      openDeals: [],
      lostDeals: [],
      loading: false,
      error
    };
  }
}
